/**
 * HenniPenny auth — a thin client for Seitiate+ login.
 *
 * HenniPenny does NOT own authentication. Login happens on **seitiate.app**; the
 * Seitiate engine issues the `cesauth_` JWT. This module only:
 *   1. stores the token + user under the SAME localStorage keys the Seitiate+
 *      surfaces use (`cesauth_token` / `cesauth_user`), so the contract matches,
 *   2. bounces an unauthenticated visitor to the seitiate.app login with a
 *      `return_to` back to our `/auth/callback`, and
 *   3. consumes the token handed back at `/auth/callback`.
 *
 * Handoff is via the URL **fragment** (`#token=…`), never the query string, so the
 * token is never sent to a server or written to access logs. The `return_to`
 * allowlist that stops the token being handed to an arbitrary callback lives on
 * seitiate.app (a paired change in the Seitiate+ repo must allow this app's
 * origin) — that is the real control.
 *
 * The NILM workbench itself needs no session: it parses CSVs and computes metrics
 * entirely in the browser. Sign-in exists so a researcher lands in *their*
 * HenniPenny space and can carry sealed evidence into their Evidence Foundry
 * tenant; it never gates viewing or running the in-browser preview.
 */

const TOKEN_KEY = 'cesauth_token';
const USER_KEY = 'cesauth_user';
const VERTICAL_KEY = 'cesauth_vertical';
const POST_LOGIN_PATH_KEY = 'hennipenny_post_login_path';

/** This vertical's id, recorded on the session when login doesn't return one. */
const VERTICAL_ID = 'hennipenny';

/** The login surface on seitiate.app. Overridable for dev/staging. */
const LOGIN_URL =
  (import.meta.env.VITE_SEITIATE_LOGIN_URL as string | undefined)?.trim() ||
  'https://seitiate.app/login';

/** Real path (not a hash route) so seitiate.app can redirect straight back here. */
export const CALLBACK_PATH = '/auth/callback';

/** The hash route the app opens to after a successful sign-in. */
export const POST_LOGIN_DEFAULT = '/#/workbench';

/** Subset of the Seitiate+ AuthUser that HenniPenny actually uses. */
export interface HenniPennyUser {
  uid: string;
  email: string;
  name?: string | null;
  tenantId?: string | null;
  engineUserId?: string | null;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getUser(): HenniPennyUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as HenniPennyUser) : null;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return Boolean(getToken());
}

export function setSession(token: string, user: HenniPennyUser | null): void {
  localStorage.setItem(TOKEN_KEY, token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(VERTICAL_KEY);
}

/** Absolute URL of our callback on the current origin (works in dev/staging/prod). */
export function callbackUrl(): string {
  return `${window.location.origin}${CALLBACK_PATH}`;
}

function currentReturnPath(): string {
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.pathname.startsWith(CALLBACK_PATH)) return POST_LOGIN_DEFAULT;
  return path === '/' ? POST_LOGIN_DEFAULT : path;
}

export function postLoginPath(): string {
  try {
    const stored = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
    sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
    return stored || POST_LOGIN_DEFAULT;
  } catch {
    return POST_LOGIN_DEFAULT;
  }
}

export function loginRedirectUrl(): string {
  try {
    sessionStorage.setItem(POST_LOGIN_PATH_KEY, currentReturnPath());
  } catch {
    /* non-fatal */
  }
  return `${LOGIN_URL}?return_to=${encodeURIComponent(callbackUrl())}`;
}

/** Send an unauthenticated visitor to seitiate.app login, return_to our callback. */
export function redirectToLogin(): void {
  window.location.assign(loginRedirectUrl());
}

// Sign-out must land the user on a surface that will NOT silently re-authenticate.
// Clearing HenniPenny's local auth and going to "/" is not enough: the platform
// session at seitiate.app is still alive, so the redirectToLogin → `?return_to=…`
// round-trip re-issues a token and drops the user straight back into the workbench
// ("Sign out does nothing"). seitiate-plus #161 shipped a `?switch=1` escape on the
// shared login page that (a) forces a fresh login form even when a session is cached —
// no auto return_to redirect — and (b) runs clearAuth() in the seitiate.app origin on
// arrival, ending the platform session itself. Sending sign-out there breaks the
// re-auth loop. No `return_to`: sign-out is a signed-out surface, not a bounce-back.
export function signOutUrl(): string {
  return `${LOGIN_URL}?switch=1`;
}

/** Sign out locally, then land on the #161 account-switch surface (not a re-auth loop). */
export function signOut(): void {
  clearAuth();
  window.location.assign(signOutUrl());
}

export interface CallbackResult {
  ok: boolean;
  error?: string;
}

/**
 * Consume the token seitiate.app handed back in the fragment after login, store
 * the session, and scrub the token from the URL/history. Call only on the
 * /auth/callback path.
 */
export function consumeAuthCallback(): CallbackResult {
  const fragment = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : '';
  const params = new URLSearchParams(fragment);
  const token = params.get('token');
  if (!token) {
    // Already consumed (React StrictMode re-runs the callback effect in dev) or a
    // signed-in user navigated here directly: the fragment was already scrubbed,
    // so a stored session means success — not a false "no token" error.
    if (isAuthenticated()) return { ok: true };
    return { ok: false, error: 'Sign-in did not return a token.' };
  }

  let user: HenniPennyUser | null = null;
  const rawUser = params.get('user');
  if (rawUser) {
    try {
      user = JSON.parse(rawUser) as HenniPennyUser;
    } catch {
      user = null; // the token is enough; user detail is best-effort
    }
  }

  setSession(token, user);
  localStorage.setItem(VERTICAL_KEY, params.get('vertical') || VERTICAL_ID);
  // Remove the token from the address bar + history immediately. Keep the
  // callback path until AuthCallback redirects; scrubbing to "/" first makes
  // the next "/#/..." redirect hash-only and leaves the callback component
  // mounted.
  try {
    window.history.replaceState(null, '', `${window.location.origin}${CALLBACK_PATH}`);
  } catch {
    /* non-fatal */
  }
  return { ok: true };
}
